import type { Option } from "@tento-lona/sheets";
import type { TypedApi } from "@tento-core/typed-api";
import { Tracing } from "@tento-core/tracing";
import type { DateTime, Utc } from "@tento-chrono";
import { UtcDays } from "@tento-lona/sheets/internal-sdk";
import { type CellId, RowKey, RowNetworkKey } from "@tento-lona/sheets";
import type { Json } from "@tento-lona/sheets";

import type { Network } from "../network.ts";
import { CellWriteError, SdkRequestError } from "../errors.ts";
import type {
  CellSnapshot,
  MutationBackend,
} from "@tento-lona/sheets/mutation";
import type {
  MutationAck,
  PendingMutation,
} from "@tento-lona/sheets/mutation";

/**
 * The `POST /api/rows/{rowKey}/cells` request one op produces — the exact shape
 * the connector expects today. Domains build this from their op in `bodyFor`,
 * keeping the SDK op op-shaped while the connector keeps its cell-ish contract;
 * a CRDT backend would translate the same op to a room submit instead.
 */
export interface CloudPost {
  readonly rowKey: RowNetworkKey;
  /** The cell write; its `data: null` deletes an id-addressed cell. */
  readonly body: Network.CellWriteRequest;
}

/**
 * Cloud-HTTP `MutationBackend` base, generic over a domain's `Op` + `Item`.
 * `submit` does the shared `POST /api/rows/{rowKey}/cells` + decode + `MutationAck`;
 * the two domain-specific bits are injected: `bodyFor(op)` (the op→POST
 * translation, kept on the SDK side so the op stays op-shaped — the CRDT door) and
 * `decodeCanonical` (wire cell → the domain `Item`). A domain subclasses this and
 * passes its `bodyFor` (see `event-mutations.ts`).
 *
 * `snapshot` (the base fetch, checked: it throws `SdkRequestError` rather
 * than resolving an empty base) is wired per-domain when the read path moves onto
 * the overlay (Move 4 P2); base is supplied by the existing fetch path until then.
 */
export class CloudMutationBackend<Op, Item, Id extends string>
  implements MutationBackend<Op, Item, Id> {
  readonly #api: TypedApi<Network.Routes>;
  readonly #decodeCanonical: (raw: Json) => Option<Item>;
  readonly #bodyFor: (op: Op, rowKey: RowNetworkKey) => CloudPost;
  readonly #keyOf: (cellId: CellId) => Id;
  /**
   * `bodyFor` translates one semantic op into the `POST /api/rows/{rowKey}/cells`
   * request (the domain's half, e.g. `eventBodyFor`); `decodeCanonical` turns a
   * wire cell into the domain `Item`; `keyOf` names a cell's item by its id
   * `Id`, the key the snapshot holds it under (an event's `EventId` is its
   * cell's id: `(cellId) => EventId.of(cellId)`).
   */
  constructor(
    api: TypedApi<Network.Routes>,
    decodeCanonical: (raw: Json) => Option<Item>,
    bodyFor: (op: Op, rowKey: RowNetworkKey) => CloudPost,
    keyOf: (cellId: CellId) => Id,
  ) {
    this.#api = api;
    this.#decodeCanonical = decodeCanonical;
    this.#bodyFor = bodyFor;
    this.#keyOf = keyOf;
  }

  /**
   * Send one op. A rejected or unanswered POST THROWS `CellWriteError` (step
   * by the body's shape: `write-timepoint-cell` for a datetime-ranged cell,
   * `clear-timepoint-cell` for an id-addressed delete, else `write-cell` /
   * `clear-cell`), so `MutationController.settle` drops the optimistic append; it
   * never resolves a failure as an ack. `canonical` is `null` only for the
   * delete ack (an id-addressed `data: null` body), which carries no cell.
   */
  async submit(m: PendingMutation<Op>): Promise<MutationAck<Item>> {
    const post = this.#bodyFor(m.op, m.rowKey);
    const isDelete = post.body.id != null && post.body.data == null;
    const step: CellWriteError.Step = post.body.endDate != null
      ? "write-timepoint-cell"
      : isDelete && post.body.date == null
      ? "clear-timepoint-cell"
      : post.body.data == null
      ? "clear-cell"
      : "write-cell";
    // after-failed-write: a MutationBackend's submit; the controller drops the append and its caller recovers (EventsClient re-reads the row).
    const resp = await CellWriteError.check(
      step,
      "POST",
      "/api/rows/{rowKey}/cells",
      () =>
        this.#api.postResult("/api/rows/{rowKey}/cells", {
          path: { rowKey: RowNetworkKey.fromWire(post.rowKey) },
          body: post.body,
        }),
    );
    const raw = resp.cells[0]?.data;
    const canonical = raw != null ? this.#decodeCanonical(raw) : null;
    if (canonical == null && !isDelete) {
      Tracing.warn("net")("cloud-mutation/ack-without-cell", {
        rowKey: post.rowKey,
        clientId: m.clientId,
      });
    }
    return { clientId: m.clientId, canonical };
  }

  /**
   * Fetch the authoritative base for a row+range: `GET /api/rows/{rowKey}/cells`,
   * each cell decoded to the domain `Item` via the injected `decodeCanonical`.
   * Keyed by the wire cell id (`cell.id`) — the canonical key the overlay base is
   * keyed by (for events `cell.id === event.id === EventItem.id`). The overlay's
   * own pending log is preserved across a `setBase(snapshot(...))`, so in-flight
   * optimism survives a base refresh.
   */
  async snapshot(
    rowKey: RowNetworkKey,
    range: DateTime.Range<Utc>,
  ): Promise<CellSnapshot<Item, Id>> {
    // Checked: a failed read must not become an empty base, which would
    // erase every confirmed item in the range on `setBase`. Every UTC day
    // the range touches (`UtcDays.touched`, the one rule every cell read
    // names), for the same reason: a dropped last day would read as no
    // confirmed items there.
    const days = UtcDays.touched(range);
    const cells = await SdkRequestError.checkRequest(
      "cells",
      "get-cells",
      "GET",
      "/api/rows/{rowKey}/cells",
      () =>
        this.#api.getResult("/api/rows/{rowKey}/cells", {
          path: { rowKey: RowNetworkKey.forRead(RowNetworkKey.fromWire(rowKey)) },
          query: {
            start: days.start,
            end: days.end,
            mock_sync_delay: null,
            booking_id: null,
          },
        }),
    );
    const out = new Map<Id, Item>();
    for (const cell of cells) {
      if (cell.id == null || cell.data == null) continue;
      const item = this.#decodeCanonical(cell.data);
      if (item != null) out.set(this.#keyOf(cell.id), item);
    }
    return out;
  }
}
